import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { eventsApi, phasesApi, speakersApi, microphonesApi, camerasApi, timerApi, commentsApi, screenValuesApi, screensApi } from '../lib/api';
import { useSocket } from '../hooks/useSocket';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useUndoRedo } from '../hooks/useUndoRedo';
import { formatTime, calculateTotalDuration, getPhaseTypeLabel, getPhaseTypeColor } from '../lib/utils';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import PhaseRow from '../components/PhaseRow';
import TimerPanel from '../components/TimerPanel';
import PhaseDialog from '../components/PhaseDialog';
import SpeakersDialog from '../components/SpeakersDialog';
import MicrophonesDialog from '../components/MicrophonesDialog';
import CamerasDialog from '../components/CamerasDialog';
import EventSettingsDialog from '../components/EventSettingsDialog';
import StreamingModal from '../components/StreamingModal';
import ClientsDialog from '../components/ClientsDialog';
import CommentsPanel from '../components/CommentsPanel';
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  ArrowLeft,
  Sun,
  Moon,
  Undo2,
  Redo2,
  Users,
  ChevronLeft,
  ChevronRight,
  Save,
  Mic,
  Monitor,
  Tv,
  MessageSquare,
  UserPlus,
  SlidersHorizontal,
  Clock,
  Settings,
  TrendingUp,
  TrendingDown,
  Copy,
  CheckCircle,
  XCircle,
  MoreVertical,
  GitBranch,
} from 'lucide-react';

const buildGridTemplate = (vis, visibleScreens = []) => [
  '40px',
  'minmax(160px, 1fr)',
  '120px',
  ...(vis.onStage ? ['140px'] : []),
  ...(vis.mic     ? ['100px'] : []),
  ...visibleScreens.map(() => '90px'),
  ...(vis.est     ? ['64px']  : []),
  ...(vis.real    ? ['64px']  : []),
  ...(vis.diff    ? ['64px']  : []),
  '44px',
  '44px',
].join(' ');

const calcTableMinWidth = (vis, screenCount) => {
  let w = 40 + 160 + 120 + 44 + 44; // handle + fase-min + info + comments + actions
  if (vis.onStage) w += 140;
  if (vis.mic)     w += 100;
  w += screenCount * 90;
  if (vis.est)  w += 64;
  if (vis.real) w += 64;
  if (vis.diff) w += 64;
  return w;
};

const COL_LABELS = [
  { key: 'onStage', label: 'On Stage' },
  { key: 'mic',     label: 'Mic' },
  { key: 'est',     label: 'Estimado' },
  { key: 'real',    label: 'Real' },
  { key: 'diff',    label: 'Diferencia' },
];

export default function EventEditorPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { logout, user } = useAuth();

  const [event, setEvent] = useState(null);
  const [phases, setPhases] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [microphones, setMicrophones] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timerRunning, setTimerRunning] = useState(false);
  const [totalElapsed, setTotalElapsed] = useState(0);
  const [phaseElapsed, setPhaseElapsed] = useState(0);
  const [showPhaseDialog, setShowPhaseDialog] = useState(false);
  const [showSpeakersDialog, setShowSpeakersDialog] = useState(false);
  const [showMicrophonesDialog, setShowMicrophonesDialog] = useState(false);
  const [showCamerasDialog, setShowCamerasDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showStreamingModal, setShowStreamingModal] = useState(false);
  const [showClientsDialog, setShowClientsDialog] = useState(false);
  const [showCommentsPanel, setShowCommentsPanel] = useState(false);
  const [comments, setComments] = useState([]);
  const [highlightPhaseId, setHighlightPhaseId] = useState(null);
  const [editingPhase, setEditingPhase] = useState(null);
  const [subPhaseParentId, setSubPhaseParentId] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showTimerPanel, setShowTimerPanel] = useState(true);
  const [visibleCols, setVisibleCols] = useState({
    onStage: true, mic: true, est: true, real: true, diff: true,
  });
  const [hiddenScreenIds, setHiddenScreenIds] = useState(new Set());

  const { state: historyPhases, setState: setHistoryPhases, undo, redo, canUndo, canRedo, reset: resetHistory } = useUndoRedo([]);

  // Socket handlers
  const socketHandlers = useMemo(() => ({
    onEventUpdated: (data) => {
      setEvent(data);
      setTimerRunning(data.timer_running);
      setTotalElapsed(data.timer_elapsed);
    },
    onPhaseCreated: (phase) => {
      setPhases(prev => [...prev, phase].sort((a, b) => a.order - b.order));
    },
    onPhaseUpdated: (phase) => {
      setPhases(prev => prev.map(p => p.id === phase.id ? phase : p));
    },
    onPhaseDeleted: ({ id }) => {
      setPhases(prev => prev.filter(p => p.id !== id));
    },
    onPhasesReordered: (newPhases) => {
      setPhases(newPhases);
    },
    onTimerAction: ({ event: ev, phases: ph }) => {
      setEvent(ev);
      setPhases(ph);
      setTimerRunning(ev.timer_running);
    },
    onCommentCreated: (comment) => {
      setComments(prev =>
        prev.some(c => c.id === String(comment.id)) ? prev : [...prev, comment]
      );
    },
    onCommentDeleted: ({ id }) => {
      setComments(prev => prev.filter(c => c.id !== String(id)));
    },
    onCommentUpdated: (comment) => {
      setComments(prev => prev.map(c => c.id === String(comment.id) ? comment : c));
    },
    onScreensUpdated: () => {
      screensApi.getAll(eventId).then(res => setScreens(res.data)).catch(() => {});
    },
  }), [eventId]);

  useSocket(eventId, socketHandlers);

  // DnD sensors - Configured for fast, responsive drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,  // Reduced from 8 for faster activation
        tolerance: 5
      }
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventRes, phasesRes, speakersRes, micsRes, camerasRes, screensRes] = await Promise.all([
          eventsApi.getOne(eventId),
          phasesApi.getAll(eventId),
          speakersApi.getAll(eventId),
          microphonesApi.getAll(eventId),
          camerasApi.getAll(eventId),
          screensApi.getAll(eventId),
        ]);
        // Redirect clients to their view
        if (eventRes.data.my_role === 'client') {
          navigate(`/event/${eventId}/client`, { replace: true });
          return;
        }
        setEvent(eventRes.data);
        setPhases(phasesRes.data);
        setSpeakers(speakersRes.data);
        setMicrophones(micsRes.data);
        setCameras(camerasRes.data);
        setScreens(screensRes.data);
        setTimerRunning(eventRes.data.timer_running);
        setTotalElapsed(eventRes.data.timer_elapsed || 0);
        resetHistory(phasesRes.data);
        // Load comments
        try {
          const commentsRes = await commentsApi.getAll(eventId);
          setComments(commentsRes.data);
        } catch (_) {}
      } catch (error) {
        toast.error('Error al cargar el evento');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [eventId, navigate, resetHistory]);

  // Poll comments every 5 seconds so phase badges update even with panel closed
  useEffect(() => {
    if (!eventId) return;
    const interval = setInterval(async () => {
      try {
        const res = await commentsApi.getAll(eventId);
        setComments(res.data);
      } catch (_) {}
    }, 5000);
    return () => clearInterval(interval);
  }, [eventId]);

  // Timer interval
  useEffect(() => {
    let interval;
    if (timerRunning) {
      interval = setInterval(() => {
        setTotalElapsed(prev => prev + 1);
        setPhaseElapsed(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerRunning]);

  // Clock ticker for schedule drift when timer is not running (pre-start or paused)
  // Forces a re-render every second so Date.now() in scheduleDrift stays current
  const [, forceClockTick] = useState(0);
  useEffect(() => {
    if (timerRunning || !event?.scheduled_date) return;
    const interval = setInterval(() => forceClockTick(n => n + 1), 1000);
    return () => clearInterval(interval);
  }, [timerRunning, event?.scheduled_date]);

  // Auto-save elapsed time
  useEffect(() => {
    if (timerRunning && totalElapsed % 5 === 0) {
      timerApi.updateElapsed(eventId, totalElapsed, phaseElapsed).catch(() => { });
    }
  }, [totalElapsed, phaseElapsed, timerRunning, eventId]);

  // Timer actions
  const handlePlay = async () => {
    try {
      let initialElapsed;
      // Only calculate offset on first start (timer hasn't run yet)
      if (totalElapsed === 0 && event?.scheduled_date) {
        const offsetSeconds = Math.floor((Date.now() - new Date(event.scheduled_date).getTime()) / 1000);
        if (offsetSeconds > 0) initialElapsed = offsetSeconds;
      }
      const res = await timerApi.action(eventId, 'play', initialElapsed);
      setEvent(res.data.event);
      setPhases(res.data.phases);
      setTimerRunning(true);
      setTotalElapsed(res.data.event.timer_elapsed || 0);
      if (initialElapsed > 0) {
        const mins = Math.floor(initialElapsed / 60);
        const secs = initialElapsed % 60;
        const parts = [];
        if (mins > 0) parts.push(`${mins} min`);
        if (secs > 0) parts.push(`${secs} seg`);
        toast.warning(`Inicio con ${parts.join(' ')} de retraso sobre lo programado`);
      } else {
        toast.success('Timer iniciado');
      }
    } catch (error) {
      toast.error('Error al iniciar timer');
    }
  };

  const handlePause = async () => {
    try {
      await timerApi.action(eventId, 'pause');
      setTimerRunning(false);
      toast.success('Timer pausado');
    } catch (error) {
      toast.error('Error al pausar timer');
    }
  };

  const handleReset = async () => {
    if (!window.confirm('¿Reiniciar todo el evento?')) return;
    try {
      const res = await timerApi.action(eventId, 'reset');
      setEvent(res.data.event);
      setPhases(res.data.phases);
      setTimerRunning(false);
      setTotalElapsed(0);
      setPhaseElapsed(0);
      toast.success('Evento reiniciado');
    } catch (error) {
      toast.error('Error al reiniciar');
    }
  };

  const handleNext = async () => {
    try {
      const res = await timerApi.action(eventId, 'next');
      setEvent(res.data.event);
      setPhases(res.data.phases);
      setPhaseElapsed(0);
    } catch (error) {
      toast.error('Error al avanzar');
    }
  };

  const handlePrev = async () => {
    try {
      const res = await timerApi.action(eventId, 'prev');
      setEvent(res.data.event);
      setPhases(res.data.phases);
      setPhaseElapsed(0);
    } catch (error) {
      toast.error('Error al retroceder');
    }
  };

  // Keyboard shortcuts
  useKeyboardShortcuts([
    { key: ' ', action: () => timerRunning ? handlePause() : handlePlay() },
    { key: 'ArrowRight', action: handleNext },
    { key: 'ArrowLeft', action: handlePrev },
    { key: 'z', ctrl: true, action: () => canUndo && handleUndo() },
    { key: 'y', ctrl: true, action: () => canRedo && handleRedo() },
  ]);

  // Undo/Redo
  const handleUndo = async () => {
    const prevPhases = undo();
    if (prevPhases) {
      try {
        await phasesApi.reorder(eventId, prevPhases.map(p => p.id));
        setPhases(prevPhases);
      } catch (error) {
        toast.error('Error al deshacer');
      }
    }
  };

  const handleRedo = async () => {
    const nextPhases = redo();
    if (nextPhases) {
      try {
        await phasesApi.reorder(eventId, nextPhases.map(p => p.id));
        setPhases(nextPhases);
      } catch (error) {
        toast.error('Error al rehacer');
      }
    }
  };

  // Drag & Drop
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (active.id !== over?.id) {
      const oldIndex = phases.findIndex(p => p.id === active.id);
      const newIndex = phases.findIndex(p => p.id === over.id);
      const newPhases = arrayMove(phases, oldIndex, newIndex);

      setPhases(newPhases);
      setHistoryPhases(newPhases);

      try {
        await phasesApi.reorder(eventId, newPhases.map(p => p.id));
      } catch (error) {
        toast.error('Error al reordenar');
        setPhases(phases);
      }
    }
  };

  // Event actions
  const handleFinalizeEvent = async () => {
    if (!window.confirm('¿Marcar este evento como finalizado?')) return;
    try {
      await eventsApi.update(eventId, { status: 'completed' });
      navigate('/dashboard');
    } catch { toast.error('Error al finalizar evento'); }
  };

  const handleCancelEvent = async () => {
    if (!window.confirm('¿Marcar este evento como cancelado?')) return;
    try {
      await eventsApi.update(eventId, { status: 'cancelled' });
      navigate('/dashboard');
    } catch { toast.error('Error al cancelar evento'); }
  };

  const handleDuplicateEvent = async () => {
    try {
      const res = await eventsApi.duplicate(eventId);
      toast.success('Evento duplicado');
      navigate(`/event/${res.data.id}`);
    } catch { toast.error('Error al duplicar evento'); }
  };

  // Phase CRUD
  const handleCreatePhase = async (data, screenVals = {}) => {
    try {
      const parentIdVal = subPhaseParentId || data.parent_id || null;
      const payload = parentIdVal ? { ...data, parent_id: parentIdVal } : data;
      const res = await phasesApi.create(eventId, payload);
      let newPhase = res.data;
      if (Object.keys(screenVals).length > 0) {
        try {
          const svRes = await screenValuesApi.update(eventId, newPhase.id, screenVals);
          newPhase = svRes.data;
        } catch (_) {}
      }
      setPhases([...phases, newPhase]);
      setShowPhaseDialog(false);
      setSubPhaseParentId(null);
      toast.success(parentIdVal ? 'Subfase creada' : 'Fase creada');
    } catch (error) {
      toast.error('Error al crear fase');
    }
  };

  const handleUpdatePhase = async (phaseId, data, screenVals = {}) => {
    try {
      const res = await phasesApi.update(eventId, phaseId, data);
      let updatedPhase = res.data;
      if (Object.keys(screenVals).length > 0 || Object.keys(editingPhase?.screen_values || {}).length > 0) {
        try {
          const svRes = await screenValuesApi.update(eventId, phaseId, screenVals);
          updatedPhase = svRes.data;
        } catch (_) {}
      }
      setPhases(phases.map(p => p.id === phaseId ? updatedPhase : p));
      setEditingPhase(null);
      toast.success('Fase actualizada');
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleDeletePhase = async (phaseId) => {
    if (!window.confirm('¿Eliminar esta fase?')) return;
    try {
      await phasesApi.delete(eventId, phaseId);
      setPhases(phases.filter(p => p.id !== phaseId));
      toast.success('Fase eliminada');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleDuplicatePhase = async (phaseId) => {
    try {
      const res = await phasesApi.duplicate(eventId, phaseId);
      const updatedPhases = await phasesApi.getAll(eventId);
      setPhases(updatedPhases.data);
      toast.success('Fase duplicada');
    } catch (error) {
      toast.error('Error al duplicar');
    }
  };

  // Speaker change from table
  const handleSpeakerChange = async (phaseId, speakerId) => {
    try {
      await phasesApi.update(eventId, phaseId, { speaker_id: speakerId || null });
      setPhases(phases.map(p => p.id === phaseId ? { ...p, speaker_id: speakerId } : p));
    } catch (error) {
      toast.error('Error al cambiar ponente');
    }
  };

  // Current phase
  const currentPhase = phases.find(p => p.id === event?.current_phase_id);
  const currentPhaseIndex = phases.findIndex(p => p.id === event?.current_phase_id);
  const nextPhase = currentPhaseIndex >= 0 ? phases[currentPhaseIndex + 1] : phases[0];

  // Calculations
  const totalDuration = calculateTotalDuration(phases);
  const totalActual = phases.reduce((acc, p) => acc + (p.actual_duration || 0), 0);

  // Schedule drift: real-time seconds the event is behind (positive) or ahead (negative) of plan.
  // Uses Date.now() so it updates live, including before the event starts and during pauses.
  // phaseElapsed is capped at the active phase's duration so that once a phase runs over its estimate
  // the drift grows second by second (since Date.now() keeps advancing but the cap stays fixed).
  const sumCompletedEstimates = phases
    .filter(p => p.status === 'completed')
    .reduce((acc, p) => acc + (p.duration_seconds || 0), 0);
  const activePhaseObj = phases.find(p => p.id === event?.current_phase_id);
  const phaseCapElapsed = activePhaseObj
    ? Math.min(phaseElapsed, activePhaseObj.duration_seconds)
    : 0;
  const scheduleDrift = (() => {
    if (!event?.scheduled_date) return null;
    const scheduledMs = new Date(event.scheduled_date).getTime();
    const now = Date.now();
    if (now <= scheduledMs) return null; // event hasn't reached its scheduled start yet
    return Math.floor((now - scheduledMs) / 1000) - sumCompletedEstimates - phaseCapElapsed;
  })();

  const visibleScreens = screens.filter(s => !hiddenScreenIds.has(String(s.id)));

  const activePhase = phases.find(p => p.id === activeId);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="event-editor-page">
      {/* Header */}
      <header className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-50">
        <div className="px-3 md:px-6 py-2 md:py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-1 md:gap-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="font-heading text-base md:text-lg font-bold truncate">{event?.name}</h1>
              <p className="text-xs text-muted-foreground">{phases.length} fases · {formatTime(totalDuration)}</p>
            </div>
          </div>

          <div className="flex items-center gap-1 md:gap-2 justify-end">
            <Button variant="ghost" size="icon" onClick={handleUndo} disabled={!canUndo} title="Deshacer (Ctrl+Z)" className="hidden sm:inline-flex">
              <Undo2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleRedo} disabled={!canRedo} title="Rehacer (Ctrl+Y)" className="hidden sm:inline-flex">
              <Redo2 className="w-4 h-4" />
            </Button>
            <Separator orientation="vertical" className="h-6 hidden sm:block" />
            <Button variant="ghost" size="icon" onClick={() => setShowClientsDialog(true)} title="Gestionar clientes" data-testid="clients-btn" className="hidden md:inline-flex">
              <UserPlus className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setShowCommentsPanel(p => !p)} title="Comentarios" data-testid="comments-btn" className="hidden md:inline-flex">
              <MessageSquare className="w-4 h-4" />
            </Button>
            <Separator orientation="vertical" className="h-6 hidden md:block" />
            <Button variant="ghost" size="icon" onClick={() => setShowSpeakersDialog(true)} data-testid="speakers-btn" className="hidden md:inline-flex">
              <Users className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setShowMicrophonesDialog(true)} data-testid="microphones-btn" className="hidden md:inline-flex">
              <Mic className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setShowSettingsDialog(true)} data-testid="settings-btn" title="Ajustes del evento">
              <Settings className="w-4 h-4" />
            </Button>
            {event?.streaming_url && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowStreamingModal(true)}
                data-testid="streaming-btn"
                title="Ver streaming en vivo"
                className="hidden sm:inline-flex"
              >
                <Tv className="w-4 h-4" />
              </Button>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" title="Mostrar/ocultar columnas">
                  <SlidersHorizontal className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56 p-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Columnas</p>
                <div className="space-y-2">
                  {COL_LABELS.map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-2">
                      <Checkbox
                        id={`col-${key}`}
                        checked={visibleCols[key]}
                        onCheckedChange={(checked) => setVisibleCols(prev => ({ ...prev, [key]: !!checked }))}
                      />
                      <Label htmlFor={`col-${key}`} className="text-sm font-normal cursor-pointer">{label}</Label>
                    </div>
                  ))}
                  {screens.length > 0 && (
                    <>
                      <div className="border-t border-border pt-2 mt-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Checkbox
                            id="col-screens-all"
                            checked={screens.every(s => !hiddenScreenIds.has(String(s.id)))}
                            onCheckedChange={(checked) => {
                              if (checked) setHiddenScreenIds(new Set());
                              else setHiddenScreenIds(new Set(screens.map(s => String(s.id))));
                            }}
                          />
                          <Label htmlFor="col-screens-all" className="text-sm font-medium cursor-pointer">Recursos</Label>
                        </div>
                        {screens.map(screen => (
                          <div key={screen.id} className="flex items-center gap-2 pl-5">
                            <Checkbox
                              id={`col-screen-${screen.id}`}
                              checked={!hiddenScreenIds.has(String(screen.id))}
                              onCheckedChange={(checked) => {
                                setHiddenScreenIds(prev => {
                                  const next = new Set(prev);
                                  if (checked) next.delete(String(screen.id));
                                  else next.add(String(screen.id));
                                  return next;
                                });
                              }}
                            />
                            <Label htmlFor={`col-screen-${screen.id}`} className="text-sm font-normal cursor-pointer truncate">{screen.name}</Label>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <Button
              variant={showTimerPanel ? 'secondary' : 'ghost'}
              size="icon"
              onClick={() => setShowTimerPanel(p => !p)}
              title={showTimerPanel ? 'Ocultar panel de fase' : 'Panel de fase actual'}
            >
              <Clock className="w-4 h-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" title="Más opciones">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleDuplicateEvent}>
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicar evento
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {event?.status !== 'completed' && event?.status !== 'cancelled' && (
                  <DropdownMenuItem className="text-green-500" onClick={handleFinalizeEvent}>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Finalizar evento
                  </DropdownMenuItem>
                )}
                {event?.status !== 'cancelled' && (
                  <DropdownMenuItem className="text-destructive" onClick={handleCancelEvent}>
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancelar evento
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex relative overflow-hidden">
        {/* Main content - Rundown table */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Timer controls bar */}
          <div className="border-b border-border px-3 md:px-6 py-2 md:py-4 flex flex-wrap items-center justify-between gap-2 bg-card">
            <div className="flex items-center gap-2 md:gap-4">
              <div className="flex items-center gap-1 md:gap-2">
                <Button
                  size="sm"
                  variant={timerRunning ? "secondary" : "default"}
                  onClick={timerRunning ? handlePause : handlePlay}
                  className="gap-1.5 md:gap-2"
                  data-testid="play-pause-btn"
                >
                  {timerRunning ? (
                    <>
                      <Pause className="w-5 h-5" />
                      <span className="hidden sm:inline">Pausar</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5" />
                      <span className="hidden sm:inline">Iniciar</span>
                    </>
                  )}
                </Button>
                <Button variant="outline" size="sm" onClick={handleReset} data-testid="reset-btn">
                  <RotateCcw className="w-4 h-4" />
                </Button>
              </div>

              <Separator orientation="vertical" className="h-6 md:h-8" />

              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={handlePrev} disabled={currentPhaseIndex <= 0}>
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button variant="ghost" size="icon" onClick={handleNext} disabled={currentPhaseIndex >= phases.length - 1}>
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-3 md:gap-6">
              <div className="text-right">
                <div className="text-[10px] md:text-xs text-muted-foreground uppercase tracking-wider">Tiempo total</div>
                <div className="font-mono text-lg md:text-2xl font-bold tabular-nums">{formatTime(totalElapsed, true)}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] md:text-xs text-muted-foreground uppercase tracking-wider">Estimado</div>
                <div className="font-mono text-lg md:text-2xl font-bold tabular-nums text-muted-foreground">{formatTime(totalDuration, true)}</div>
              </div>
              {scheduleDrift !== null && (
                <div className={`text-right px-2 md:px-3 py-1 md:py-1.5 rounded-lg border ${
                  scheduleDrift > 0
                    ? 'bg-red-500/10 border-red-500/30'
                    : scheduleDrift < 0
                    ? 'bg-green-500/10 border-green-500/30'
                    : 'bg-muted border-border'
                }`}>
                  <div className={`text-[10px] md:text-xs uppercase tracking-wider font-medium flex items-center gap-1 justify-end ${
                    scheduleDrift > 0 ? 'text-red-500' : scheduleDrift < 0 ? 'text-green-500' : 'text-muted-foreground'
                  }`}>
                    {scheduleDrift > 0
                      ? <><TrendingUp className="w-3 h-3" /> En contra</>
                      : scheduleDrift < 0
                      ? <><TrendingDown className="w-3 h-3" /> A favor</>
                      : 'En hora'}
                  </div>
                  <div className={`font-mono text-lg md:text-2xl font-bold tabular-nums ${
                    scheduleDrift > 0 ? 'text-red-500' : scheduleDrift < 0 ? 'text-green-500' : 'text-muted-foreground'
                  }`}>
                    {scheduleDrift === 0 ? '00:00' : `${scheduleDrift > 0 ? '+' : '-'}${formatTime(Math.abs(scheduleDrift), true)}`}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Phases table */}
          <div className="flex-1 overflow-auto min-h-0">
            <div className="p-3 md:p-6">
              <div style={{ minWidth: `${calcTableMinWidth(visibleCols, visibleScreens.length)}px` }}>
              {/* Table header */}
              <div
                className="grid gap-2 px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider border-b border-border mb-2"
                style={{ gridTemplateColumns: buildGridTemplate(visibleCols, visibleScreens) }}
              >
                <div></div>
                <div>Fase</div>
                <div>Info</div>
                {visibleCols.onStage && <div>On Stage</div>}
                {visibleCols.mic && <div>Mic</div>}
                {visibleScreens.map(s => (
                  <div key={s.id} className="truncate" title={s.name}>{s.name}</div>
                ))}
                {visibleCols.est && <div className="text-right">Est.</div>}
                {visibleCols.real && <div className="text-right">Real</div>}
                {visibleCols.diff && <div className="text-right">Diff</div>}
                <div className="text-center">Notas</div>
                <div></div>
              </div>

              {/* Phases list */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                {(() => {
                  const rootPhases = phases.filter(p => !p.parent_id);
                  const subPhaseMap = phases.reduce((acc, p) => {
                    if (p.parent_id) (acc[p.parent_id] = acc[p.parent_id] || []).push(p);
                    return acc;
                  }, {});
                  return (
              <SortableContext items={rootPhases.map(p => p.id)} strategy={verticalListSortingStrategy}>
                  {phases.length === 0 ? (
                    <div className="empty-state py-16">
                      <Play className="w-12 h-12 text-muted-foreground mb-4" />
                      <h3 className="font-heading text-lg font-semibold mb-2">Sin fases aún</h3>
                      <p className="text-muted-foreground mb-4">Agrega fases para construir tu rundown</p>
                      <Button onClick={() => setShowPhaseDialog(true)} className="gap-2">
                        <Plus className="w-4 h-4" />
                        Agregar fase
                      </Button>
                    </div>
                  ) : (
                    rootPhases.map((phase, index) => (
                      <Fragment key={phase.id}>
                      <PhaseRow
                        phase={phase}
                        index={index}
                        isActive={phase.id === event?.current_phase_id}
                        isNext={phase.id === nextPhase?.id && phase.id !== event?.current_phase_id}
                        phaseElapsed={phase.id === event?.current_phase_id ? phaseElapsed : phase.actual_duration}
                        speakers={speakers}
                        microphones={microphones}
                        screens={visibleScreens}
                        onEdit={() => setEditingPhase(phase)}
                        onDelete={() => handleDeletePhase(phase.id)}
                        onDuplicate={() => handleDuplicatePhase(phase.id)}
                        onAddSubPhase={() => { setSubPhaseParentId(phase.id); setEditingPhase(null); setShowPhaseDialog(true); }}
                        onSpeakerChange={(speakerId) => handleSpeakerChange(phase.id, speakerId)}
                        commentCount={comments.filter(c => c.phase_id === phase.id).length}
                        onCommentClick={(phaseId) => {
                          setHighlightPhaseId(phaseId);
                          setShowCommentsPanel(true);
                        }}
                        visibleCols={visibleCols}
                        gridTemplate={buildGridTemplate(visibleCols, visibleScreens)}
                      />
                      {(subPhaseMap[phase.id] || []).map(sub => (
                        <PhaseRow
                          key={sub.id}
                          phase={sub}
                          isSubPhase={true}
                          isActive={sub.id === event?.current_phase_id}
                          isNext={sub.id === nextPhase?.id && sub.id !== event?.current_phase_id}
                          phaseElapsed={sub.id === event?.current_phase_id ? phaseElapsed : sub.actual_duration}
                          speakers={speakers}
                          microphones={microphones}
                          screens={visibleScreens}
                          onEdit={() => setEditingPhase(sub)}
                          onDelete={() => handleDeletePhase(sub.id)}
                          onDuplicate={() => handleDuplicatePhase(sub.id)}
                          onSpeakerChange={(speakerId) => handleSpeakerChange(sub.id, speakerId)}
                          commentCount={comments.filter(c => c.phase_id === sub.id).length}
                          onCommentClick={(phaseId) => {
                            setHighlightPhaseId(phaseId);
                            setShowCommentsPanel(true);
                          }}
                          visibleCols={visibleCols}
                          gridTemplate={buildGridTemplate(visibleCols, visibleScreens)}
                        />
                      ))}
                      </Fragment>
                    ))
                  )}
                </SortableContext>
                  );
                })()}

                <DragOverlay>
                  {activePhase ? (
                    <div className="bg-card border border-border rounded-lg shadow-xl p-4 opacity-90">
                      <div className="font-medium">{activePhase.name}</div>
                      <div className="text-sm text-muted-foreground">{formatTime(activePhase.duration_seconds)}</div>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
              </div>{/* end min-width */}

              {/* Add phase button */}
              {phases.length > 0 && (
                <Button
                  variant="outline"
                  className="w-full mt-4 border-dashed"
                  onClick={() => setShowPhaseDialog(true)}
                  data-testid="add-phase-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar fase
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Timer panel - floating overlay from right */}
        <div className={`absolute top-0 right-0 bottom-0 w-80 z-30 transition-transform duration-300 shadow-xl ${showTimerPanel ? 'translate-x-0' : 'translate-x-full'}`}>
          <TimerPanel
            currentPhase={currentPhase}
            nextPhase={nextPhase}
            phaseElapsed={phaseElapsed}
            timerRunning={timerRunning}
            speakers={speakers}
            screens={screens}
            onClose={() => setShowTimerPanel(false)}
          />
        </div>
      </div>

      {/* Dialogs */}
      <PhaseDialog
        open={showPhaseDialog || !!editingPhase}
        onOpenChange={(open) => {
          if (!open) {
            setShowPhaseDialog(false);
            setEditingPhase(null);
            setSubPhaseParentId(null);
          }
        }}
        phase={editingPhase}
        parentId={subPhaseParentId}
        parentPhase={subPhaseParentId ? phases.find(p => p.id === subPhaseParentId) : null}
        speakers={speakers}
        screens={screens}
        phases={phases}
        onSubmit={editingPhase ?
          (data, screenVals) => handleUpdatePhase(editingPhase.id, data, screenVals) :
          (data, screenVals) => handleCreatePhase(data, screenVals)
        }
      />

      <SpeakersDialog
        open={showSpeakersDialog}
        onOpenChange={setShowSpeakersDialog}
        eventId={eventId}
        speakers={speakers}
        onSpeakersChange={setSpeakers}
      />

      <MicrophonesDialog
        open={showMicrophonesDialog}
        onOpenChange={setShowMicrophonesDialog}
        eventId={eventId}
        microphones={microphones}
        speakers={speakers}
        onMicrophonesChange={setMicrophones}
      />

      <CamerasDialog
        open={showCamerasDialog}
        onOpenChange={setShowCamerasDialog}
        eventId={eventId}
        cameras={cameras}
        onCamerasChange={setCameras}
      />

      <EventSettingsDialog
        open={showSettingsDialog}
        onOpenChange={setShowSettingsDialog}
        event={event}
        screens={screens}
        onEventUpdate={(updatedEvent) => setEvent(updatedEvent)}
        onScreensChange={setScreens}
      />

      <StreamingModal
        open={showStreamingModal}
        onOpenChange={setShowStreamingModal}
        streamingUrl={event?.streaming_url}
        eventName={event?.name}
      />

      <ClientsDialog
        open={showClientsDialog}
        onOpenChange={setShowClientsDialog}
        eventId={eventId}
      />

      {showCommentsPanel && (
        <CommentsPanel
          eventId={eventId}
          phases={phases}
          comments={comments}
          onCommentsChange={setComments}
          onClose={() => setShowCommentsPanel(false)}
          highlightPhaseId={highlightPhaseId}
          isOrganizer={event?.user_id === String(user?.id) || event?.user_id === user?.id}
          floating
        />
      )}

      {/* Keyboard shortcuts hint */}
      <div className="fixed bottom-4 right-4 text-xs text-muted-foreground hidden lg:flex items-center gap-4 bg-card border border-border rounded-lg px-3 py-2">
        <span><kbd className="kbd">Space</kbd> Play/Pause</span>
        <span><kbd className="kbd">←</kbd> <kbd className="kbd">→</kbd> Navegar</span>
        <span><kbd className="kbd">Ctrl+Z</kbd> Deshacer</span>
      </div>
    </div>
  );
}
